import type { ScheduledPost } from '@qlk/shared';
import { MISSING_ITEM_LABEL, SCHEDULED_POST_STATUS_LABEL, SCHEDULED_POST_TONE } from '@qlk/shared';
import { useDraggable } from '@dnd-kit/core';

import type { Tone } from '../../../ui/index.js';
import { gioVn, moTaGio } from './gioThiTruong.js';

/**
 * Nền / viền / chữ của thẻ lịch đăng.
 *
 * Dùng **lớp token** chứ không mã màu: bảng màu đổi ở `theme.css` là thẻ đổi
 * theo. Viền lấy luôn màu chữ làm mờ (`border-current opacity-25`) để mỗi tone
 * không phải khai một mã màu viền thứ ba.
 *
 * `teal` không dùng ở lịch đăng nhưng `Tone` có nó (AdSense trực tiếp), nên
 * khai cho đủ kiểu.
 */
const LOP: Record<Tone, string> = {
  ok: 'bg-ok-nen text-ok-chu',
  warn: 'bg-warn-nen text-warn-chu',
  bad: 'bg-bad-nen text-bad-chu',
  info: 'bg-info-nen text-info-chu',
  neutral: 'bg-neutral-nen text-neutral-chu',
  teal: 'bg-teal-nen text-teal-chu',
};

/** Thẻ thiếu thứ gì thì luôn hiện màu cảnh báo, kể cả khi trạng thái là nháp. */
export function tongMau(post: ScheduledPost): Tone {
  if (post.status === 'published') return 'ok';
  if (post.status === 'failed') return 'bad';
  if (post.missing.length > 0) return 'warn';
  return SCHEDULED_POST_TONE[post.status];
}

/**
 * Dòng trạng thái dưới tên video.
 *
 * ⚠️ Chỉ `scheduled_on_youtube` mới được gọi là "Đã lên lịch": lúc đó video đã
 * nằm trên YouTube và thẻ sản xuất cũng đã sang cột "Đã lên lịch". Bài `ready`
 * đủ thumbnail/nhạc/metadata nhưng thẻ **vẫn ở bước Kiểm duyệt**, nên nhãn là
 * "Chờ kiểm duyệt" chứ không phải "Sẵn sàng đăng" — nếu không trang Lịch và
 * bảng Sản xuất nói hai chuyện khác nhau (xem 12.0 mục A2).
 */
export function dongTrangThai(post: ScheduledPost): string {
  if (post.status === 'failed') return post.lastError ?? 'Lỗi khi đăng';
  if (post.missing.length > 0) {
    return post.missing.map((m) => MISSING_ITEM_LABEL[m]).join(' · ');
  }
  if (post.status === 'scheduled_on_youtube') return 'Đã lên lịch';
  return SCHEDULED_POST_STATUS_LABEL[post.status];
}

export interface TheBaiDangProps {
  post: ScheduledPost;
  onMo?: (post: ScheduledPost) => void;
  /** Bản tĩnh dùng cho DragOverlay. */
  tinh?: boolean;
}

export function TheBaiDang({ post, onMo, tinh = false }: TheBaiDangProps) {
  const keo = useDraggable({ id: post.id, data: { post }, disabled: tinh });
  const lop = LOP[tongMau(post)];

  return (
    <article
      ref={tinh ? undefined : keo.setNodeRef}
      style={{ opacity: keo.isDragging ? 0.4 : 1 }}
      className={`cursor-grab rounded-the border border-current/25 p-3 active:cursor-grabbing ${lop}`}
      title={moTaGio(post.publishAt, post.market)}
      {...(tinh ? {} : keo.attributes)}
      {...(tinh ? {} : keo.listeners)}
    >
      <button
        type="button"
        className="block w-full text-left"
        onClick={() => {
          onMo?.(post);
        }}
      >
        <span className="flex items-center justify-between gap-2">
          {/* Giờ đăng ăn theo màu chữ của tone (đã đặt ở `article`). */}
          <span className="so-lieu text-co-ban font-semibold">{gioVn(post.publishAt)}</span>
          <span className="text-phu font-semibold text-chu-mo">{post.market}</span>
        </span>

        <span className="mt-1 block text-co-ban font-semibold text-chu">{post.channelName}</span>
        <span className="mt-0.5 block text-co-ban text-chu-phu">{post.title}</span>
        {/* Dòng trạng thái cũng ăn màu chữ của tone. */}
        <span className="mt-1 block text-phu">{dongTrangThai(post)}</span>
      </button>
    </article>
  );
}
